import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users } from 'lucide-react';
import { Stack, Container, Heading, Text } from '@/components/ui/index.js';
import { HeroSection } from './components/HeroSection/index.js';
import { FeatureCard } from './components/FeatureCard/index.js';
import { StatItem } from './components/StatItem/index.js';
import styles from './LandingPage.module.css';

/**
 * LandingPage - Marketing entry point for SportsSpots
 * 
 * Features:
 * - Hero section with animated gradient text
 * - Feature highlights in glassmorphic cards
 * - Social proof with neon stat numbers
 * - Final CTA section
 */
export function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/welcome');
  };

  const handleBrowseEvents = () => {
    navigate('/discover');
  };

  const handleJoinCommunity = () => {
    navigate('/signup');
  };

  const features = [
    {
      icon: Calendar,
      title: 'Discover Events',
      description: 'Find pickup games, tournaments, and practice sessions happening near you. Filter by sport, skill level, and availability.',
    },
    {
      icon: MapPin,
      title: 'Find Venues',
      description: 'Explore local sports facilities, courts, and fields. Check availability, amenities, and ratings from other athletes.',
    },
    {
      icon: Users,
      title: 'Connect with Athletes',
      description: 'Build your sports network. Find teammates, join groups, and stay active with a community that shares your passion.',
    },
  ];

  const stats = [
    { value: '10,000+', label: 'Athletes', color: 'cyan' },
    { value: '500+', label: 'Events', color: 'lime' },
    { value: '100+', label: 'Venues', color: 'cyan' },
  ];

  return (
    <main className={styles.landing}>
      {/* Hero Section */}
      <HeroSection
        onGetStarted={handleGetStarted}
        onBrowseEvents={handleBrowseEvents}
      />

      {/* Features Section */}
      <section className={styles.features}>
        <Container size="lg" padding>
          <Stack direction="column" gap={12} align="center">
            <Stack direction="column" gap={4} align="center" className={styles.sectionHeader}>
              <Heading level={2} size="xl" color="primary" align="center">
                Everything You Need to Play
              </Heading>
              <Text size="lg" color="secondary" align="center" className={styles.sectionSubtitle}>
                Your complete athletic companion
              </Text>
            </Stack>

            <div className={styles.featureGrid}>
              {features.map((feature, index) => (
                <FeatureCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  delay={index * 150}
                />
              ))}
            </div>
          </Stack>
        </Container>
      </section>

      {/* Social Proof Section */}
      <section className={styles.socialProof}>
        <Container size="lg" padding>
          <div className={styles.statsGrid}>
            {stats.map((stat, index) => (
              <StatItem
                key={stat.label}
                value={stat.value}
                label={stat.label}
                color={stat.color}
                delay={index * 100}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaBackground}>
          <div className={styles.ctaGradient} />
        </div>
        <Container size="md" padding>
          <Stack direction="column" gap={8} align="center" className={styles.ctaContent}>
            <Stack direction="column" gap={4} align="center">
              <Heading level={2} size="xl" color="primary" align="center">
                Join the Community
              </Heading>
              <Text size="lg" color="secondary" align="center" leading="relaxed">
                Connect with thousands of athletes in your area.
                Your next game is waiting.
              </Text>
            </Stack>
            <button
              type="button"
              onClick={handleJoinCommunity}
              className={styles.ctaButton}
            >
              Sign Up Free
            </button>
          </Stack>
        </Container>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <Container size="lg" padding>
          <Stack direction="column" gap={6} align="center">
            <div className={styles.footerBrand}>
              <span className={styles.brandName}>SportsSpots</span>
            </div>
            <div className={styles.footerLinks}>
              <button
                type="button"
                onClick={() => navigate('/discover')}
                className={styles.footerLink}
              >
                Discover
              </button>
              <button
                type="button"
                onClick={() => navigate('/events')}
                className={styles.footerLink}
              >
                Events
              </button>
              <button
                type="button"
                onClick={() => navigate('/venues')}
                className={styles.footerLink}
              >
                Venues
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className={styles.footerLink}
              >
                Login
              </button>
            </div>
            <Text size="sm" color="muted" align="center">
              © 2024 SportsSpots. All rights reserved.
            </Text>
          </Stack>
        </Container>
      </footer>
    </main>
  );
}

export default LandingPage;
